import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, ArrowRight, Bot, User, Sparkles } from "lucide-react";
import { useAuth } from "../lib/auth";
import {
    getLocalisedIntents, getLocalisedQuickReplies, matchIntent, getIntentById,
    pickLang, HELPER_UI,
} from "../lib/helperBot";

/**
 * HelperBot — Adukoo's in-app assistant.
 *
 * v79.104 · rule-based, zero LLM cost, multi-language (en, hi, te, ta, es).
 * Position: bottom-LEFT (v79.104) so it never overlaps the right-side
 * chat drawer, sticky "Post an Ad" CTAs, or Funboard's reaction rail.
 * Hidden on /funboard (full-screen swipe) and /auth (sign-up flow).
 */

const STORAGE_KEY = "adukoo.helper.messages.v2";
const OPEN_KEY = "adukoo.helper.open.v2";
const NUDGE_KEY = "adukoo.helper.nudge.v2";

function loadMessages(greeting) {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        }
    } catch { /* ignore */ }
    return [{ role: "bot", text: greeting, quickReplies: true }];
}

function saveMessages(msgs) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(msgs.slice(-40))); }
    catch { /* ignore */ }
}

export default function HelperBot() {
    const { user } = useAuth();
    const lang = useMemo(() => pickLang(user), [user]);
    const ui = HELPER_UI[lang] || HELPER_UI.en;

    const [open, setOpen] = useState(() => localStorage.getItem(OPEN_KEY) === "1");
    const [messages, setMessages] = useState(() => loadMessages(ui.greeting));
    const [input, setInput] = useState("");
    const [nudged, setNudged] = useState(() => !!localStorage.getItem(NUDGE_KEY));
    const scrollRef = useRef(null);
    const navigate = useNavigate();

    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    const hidden = path === "/funboard" || path.startsWith("/auth");

    useEffect(() => { saveMessages(messages); }, [messages]);
    useEffect(() => { localStorage.setItem(OPEN_KEY, open ? "1" : "0"); }, [open]);

    // 20-second first-visit nudge (persisted so it fires once per browser)
    useEffect(() => {
        if (nudged || open || hidden) return;
        const t = setTimeout(() => {
            setNudged(true);
            localStorage.setItem(NUDGE_KEY, "1");
        }, 20000);
        return () => clearTimeout(t);
    }, [nudged, open, hidden]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, open]);

    // v79.106 — Escape key closes the drawer (desktop keyboard users).
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    const send = (text, opts = {}) => {
        const q = (text || "").trim();
        if (!q) return;
        const userMsg = { role: "user", text: q };
        const intent = opts.intentId ? getIntentById(opts.intentId, lang) : matchIntent(q, lang);
        const botMsg = intent
            ? { role: "bot", text: intent.answer, action: intent.action || null }
            : { role: "bot", text: ui.fallback, quickReplies: true };
        setMessages((prev) => [...prev, userMsg, botMsg]);
        setInput("");
    };

    const doAction = (action) => {
        if (!action) return;
        if (action.to?.startsWith("mailto:")) {
            window.location.href = action.to;
        } else {
            navigate(action.to);
            setOpen(false);
        }
    };

    const resetChat = () => {
        localStorage.removeItem(STORAGE_KEY);
        setMessages([{ role: "bot", text: ui.greeting, quickReplies: true }]);
    };

    if (hidden) return null;

    const quickReplies = getLocalisedQuickReplies(lang);

    return (
        <>
            {/* Floating launcher — BOTTOM RIGHT.
                v79.140 — Founder report: "Chatbot in android app not
                responding to touch. Placed very corner. Please fix it
                and also place slight up." Root cause: `bottom-3 right-3`
                puts the button 12 px from the edge — well INSIDE Android's
                gesture navigation zone (bottom edge = home / recent-apps
                swipe, right edge = back gesture on some devices). Touches
                near the corner get consumed by the OS and never reach
                the WebView. Fix: honour `env(safe-area-inset-bottom)`
                and lift the button ~1.5 rem above that safe area so it
                sits clear of any gesture indicator, plus `touch-action:
                manipulation` so taps aren't misinterpreted as scroll /
                edge-swipes.

                Also nudged `right` from `0.75rem` (right-3) to `1rem`
                so it's 4 px inside the safe zone on the right edge. */}
            {/* v79.212 — Founder direction: "Help button place slightly
                down and just have a small tag naming it so people know
                what it is." Lowered from 5.5 rem → 4 rem above the safe-
                area (still clears the "Made with Emergent" pill at
                z=9999 which occupies bottom-right up to ~52 px). Added a
                permanent inline "Help" pill next to the circular button
                so the affordance is discoverable on every device without
                requiring the transient nudge tooltip. */}
            {!open && (
                <div
                    className="fixed z-[110] flex items-center gap-2 flex-row-reverse funboard-helper-bubble"
                    style={{
                        bottom: "calc(env(safe-area-inset-bottom, 0px) + 4rem)",
                        right: "calc(env(safe-area-inset-right, 0px) + 1rem)",
                        touchAction: "manipulation",
                    }}
                >
                    <button
                        onClick={() => { setOpen(true); setNudged(true); localStorage.setItem(NUDGE_KEY, "1"); }}
                        data-testid="helperbot-open"
                        aria-label={ui.title}
                        style={{ touchAction: "manipulation" }}
                        className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-aduku-yellow text-aduku-navy shadow-2xl hover:scale-110 transition-transform flex items-center justify-center active:scale-95"
                    >
                        <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                    {/* v79.212 — Permanent "Help" pill (replaces the
                        transient tooltip). Rendered inline to the left of
                        the button so the label sits IN the same row and
                        never occludes the fab. Hides on very narrow
                        screens to save space but stays visible from
                        `sm:` and up. */}
                    <span
                        data-testid="helperbot-label"
                        className="hidden sm:inline-flex items-center gap-1 h-8 px-3 rounded-full bg-aduku-navy text-aduku-yellow text-[11px] font-black uppercase tracking-[0.14em] shadow-lg select-none pointer-events-none"
                    >
                        <Sparkles className="w-3 h-3" /> Help
                    </span>
                </div>
            )}

            {/* Drawer — anchored to BOTTOM RIGHT.
                v79.105 mobile: 70vh partial-height sheet with rounded top
                corners (was full-screen 100vh). The page stays visible
                behind so users never feel the app is "taken over" by the
                bot on their phone. Tap anywhere on the darkened backdrop
                closes the drawer. */}
            {open && (
                <>
                    {/* Backdrop — full-screen tap-to-close on ALL devices.
                        v79.106 (was mobile-only): founder reported desktop
                        users struggling to close the bot. Full backdrop +
                        z-index above the site header makes the close X
                        reachable and the tap-outside gesture universal. */}
                    <div
                        onClick={() => setOpen(false)}
                        data-testid="helperbot-backdrop"
                        className="fixed inset-0 z-[110] bg-black/40 backdrop-blur-sm"
                        aria-hidden="true"
                    />
                    <div
                        className="fixed z-[111] bottom-0 right-0 sm:bottom-6 sm:right-6 w-full sm:w-[380px] h-[70vh] sm:h-[560px] sm:max-h-[85vh] bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col border border-aduku-border"
                        data-testid="helperbot-drawer"
                    >
                    <header className="flex items-center gap-3 px-4 py-3 border-b border-aduku-border bg-aduku-navy text-white rounded-t-2xl">
                        <div className="w-9 h-9 rounded-full bg-aduku-yellow text-aduku-navy flex items-center justify-center shrink-0">
                            <Bot className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-black text-sm">{ui.title}</div>
                            <div className="text-[10px] text-white/60 uppercase tracking-wider">{ui.subtitle}</div>
                        </div>
                        <button
                            onClick={resetChat}
                            data-testid="helperbot-reset"
                            className="text-white/60 hover:text-white text-[10px] font-bold uppercase tracking-wider mr-2"
                        >{ui.reset}</button>
                        <button
                            onClick={() => setOpen(false)}
                            data-testid="helperbot-close"
                            aria-label="Close"
                            className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </header>

                    <div ref={scrollRef} className="flex-1 overflow-y-auto px-3.5 py-4 bg-aduku-bg space-y-3">
                        {messages.map((m, i) => (
                            <MessageBubble
                                key={i}
                                msg={m}
                                quickReplies={quickReplies}
                                onQuickReply={(id) => send(getIntentById(id, lang)?.question, { intentId: id })}
                                onAction={doAction}
                            />
                        ))}
                    </div>

                    <form
                        onSubmit={(e) => { e.preventDefault(); send(input); }}
                        className="p-3 border-t border-aduku-border bg-white sm:rounded-b-2xl flex items-center gap-2"
                    >
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={ui.placeholder}
                            data-testid="helperbot-input"
                            className="flex-1 h-10 px-3 rounded-full bg-aduku-bg border border-aduku-border text-sm focus:outline-none focus:ring-2 focus:ring-aduku-yellow/60"
                        />
                        <button
                            type="submit"
                            data-testid="helperbot-send"
                            aria-label="Send"
                            className="w-10 h-10 rounded-full bg-aduku-yellow text-aduku-navy flex items-center justify-center hover:scale-105 transition-transform"
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    </form>
                    </div>
                </>
            )}
        </>
    );
}

function MessageBubble({ msg, quickReplies, onQuickReply, onAction }) {
    const isBot = msg.role === "bot";
    return (
        <div className={`flex gap-2 ${isBot ? "" : "flex-row-reverse"}`}>
            <div className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center ${isBot ? "bg-aduku-yellow text-aduku-navy" : "bg-aduku-navy text-white"}`}>
                {isBot ? <Bot className="w-4 h-4" /> : <User className="w-4 h-4" />}
            </div>
            <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${isBot ? "bg-white border border-aduku-border text-aduku-navy" : "bg-aduku-navy text-white"}`}>
                <div className="whitespace-pre-wrap">{msg.text}</div>

                {isBot && msg.action && (
                    <button
                        onClick={() => onAction(msg.action)}
                        data-testid="helperbot-action"
                        className="mt-3 inline-flex items-center gap-1 h-8 px-3 rounded-full bg-aduku-yellow text-aduku-navy text-xs font-black hover:scale-105 transition-transform"
                    >
                        {msg.action.label} <ArrowRight className="w-3 h-3" />
                    </button>
                )}

                {isBot && msg.quickReplies && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {quickReplies.map((qr) => (
                            <button
                                key={qr.intent}
                                onClick={() => onQuickReply(qr.intent)}
                                data-testid={`helperbot-quick-${qr.intent}`}
                                className="text-[11px] px-2.5 py-1 rounded-full bg-aduku-yellow/20 hover:bg-aduku-yellow/40 text-aduku-navy font-bold border border-aduku-yellow/40 transition"
                            >
                                {qr.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
